import React from "react";
import { ReactNode } from "react";
import ObservableState from "../utils/ObservableState";
import * as ui from "../native";
import ObservableComponent from "./ObservableComponent";
import BaseUIProps, { copyBaseUIProps } from "../native/ui/BaseUIProps";
import ObjectObservable from "../utils/ObjectObservable";
import Button from "./Button";
import ScrollView2 from "./ScrollView2";
import LoginResult from "../classes/LoginResult";
import Developer from "../models/Developer";
import ButtonTextWithLoader from "./ButtonTextWithLoader";
import TextView from "./TextView";
import Query from "../classes/Query";
import LabelWithStringField from "./LabelWithStringField";
import { UsageConstants } from "../rocket/D3ETemplate";
import { BuildContext } from "../classes/BuildContext";

type _BuilderLoginScreenOnSignup = () => void;

type _BuilderLoginScreenOnForgot = () => void;

type _BuilderLoginScreenOnLoginUser = (developer: Developer) => void;

type _ForgetpasswordOnPressed = (d3eState: BuilderLoginScreenRefs) => void;

type _EnableOnPressed = (d3eState: BuilderLoginScreenRefs) => void;

type _Signup1OnPressed = (d3eState: BuilderLoginScreenRefs) => void;

type _EmailFieldOnChanged = (
  text: string,
  d3eState: BuilderLoginScreenRefs
) => void;

type _PasswordFieldOnChanged = (
  text: string,
  d3eState: BuilderLoginScreenRefs
) => void;

export interface BuilderLoginScreenProps extends BaseUIProps {
  key?: string;
  onSignup: _BuilderLoginScreenOnSignup;
  onForgot: _BuilderLoginScreenOnForgot;
  onLoginUser: _BuilderLoginScreenOnLoginUser;
}
/// To store state data for BuilderLoginScreen
class BuilderLoginScreenRefs {
  public disable: DisableState = new DisableState();
  public enable: EnableState = new EnableState();
  public forgetpassword: ForgetpasswordState = new ForgetpasswordState();
  mainScrollController: ui.ScrollController = new ui.ScrollController();
  public signup1: Signup1State = new Signup1State();
}

interface Signup1WithStateProps extends BaseUIProps {
  key?: string;
  d3eState: BuilderLoginScreenRefs;
  _onSignupHandler?: _Signup1OnPressed;
}

class Signup1State extends ObjectObservable {
  private _disable: boolean = false;
  public _hover: boolean = false;
  public _focus: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
  public get focus(): boolean {
    return this._focus;
  }
  public setFocus(val: boolean) {
    let isValChanged: boolean = this._focus !== val;

    if (!isValChanged) {
      return;
    }

    this._focus = val;

    this.fire("focus", this);
  }
}

class _Signup1WithState extends ObservableComponent<Signup1WithStateProps> {
  signup1FocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: Signup1WithStateProps) {
    super(props);

    this.initState();
  }
  public get signup1(): Signup1State {
    return this.props.d3eState.signup1;
  }
  public get d3eState(): BuilderLoginScreenRefs {
    return this.props.d3eState;
  }
  public get _onSignupHandler(): _Signup1OnPressed {
    return this.props._onSignupHandler;
  }
  public initState() {
    super.initState();

    this.updateObservable("signup1", null, this.signup1);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(
      ["signup1", "signup1.", "signup1.focus", "signup1.hover"],
      this.rebuild
    );
  }
  public signup1OnEnter(event): void {
    return this.signup1.setHover(true);
  }
  public signup1OnExit(event): void {
    return this.signup1.setHover(false);
  }
  public signup1OnFocusChange(val): void {
    return this.signup1.setFocus(val);
  }
  public dispose(): void {
    this.signup1.setHover(false);

    this.signup1.setFocus(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return Button({
      decoration: this.signup1.focus
        ? cStyle.tButtonUnderlineButtonDecorationOnFocus
        : new ui.BoxDecoration({}),
      disable: this.signup1.disable,
      onPressed: () => {
        this._onSignupHandler(this.d3eState);
      },
      onFocusChange: (val) => {
        this.signup1OnFocusChange(val);
      },
      child: TextView({ data: "Sign Up" }),
      onEnter: (event) => {
        this.signup1OnEnter(event);
      },
      onExit: (event) => {
        this.signup1OnExit(event);
      },
      className: "xe7",
    });
  }
}
function Signup1WithState(props: Signup1WithStateProps) {
  return React.createElement(_Signup1WithState, props);
}

interface DisableWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: BuilderLoginScreenRefs;
}

class DisableState extends ObjectObservable {
  private _disable: boolean = false;
  public _hover: boolean = false;
  public _focus: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
  public get focus(): boolean {
    return this._focus;
  }
  public setFocus(val: boolean) {
    let isValChanged: boolean = this._focus !== val;

    if (!isValChanged) {
      return;
    }

    this._focus = val;

    this.fire("focus", this);
  }
}

class _DisableWithState extends ObservableComponent<DisableWithStateProps> {
  disableFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: DisableWithStateProps) {
    super(props);

    this.initState();
  }
  public get disable(): DisableState {
    return this.props.d3eState.disable;
  }
  public get d3eState(): BuilderLoginScreenRefs {
    return this.props.d3eState;
  }
  public initState() {
    super.initState();

    this.updateObservable("disable", null, this.disable);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(
      ["disable", "disable.", "disable.focus", "disable.hover"],
      this.rebuild
    );
  }
  public disableOnEnter(event): void {
    return this.disable.setHover(true);
  }
  public disableOnExit(event): void {
    return this.disable.setHover(false);
  }
  public disableOnFocusChange(val): void {
    return this.disable.setFocus(val);
  }
  public dispose(): void {
    this.disable.setHover(false);

    this.disable.setFocus(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      constraints: new ui.BoxConstraints({ minWidth: 388 }),
      child: Button({
        padding: cStyle.tButtonPrimaryButtonPaddingOn,
        decoration: this.disable.hover
          ? cStyle.tButtonPrimaryButtonDecorationOnHover
          : this.disable.focus
          ? cStyle.tButtonPrimaryButtonDecorationOnFocus
          : cStyle.tButtonPrimaryButtonDecorationOn,
        disable: this.disable.disable,
        onFocusChange: (val) => {
          this.disableOnFocusChange(val);
        },
        child: ui.Wrap({
          alignment: ui.WrapAlignment.center,
          crossAxisAlignment: ui.WrapCrossAlignment.center,
          children: [ButtonTextWithLoader({ bText: "Logging In", key: "0" })],
        }),
        onEnter: (event) => {
          this.disableOnEnter(event);
        },
        onExit: (event) => {
          this.disableOnExit(event);
        },
      }),
      className: "xb0b",
    });
  }
}
function DisableWithState(props: DisableWithStateProps) {
  return React.createElement(_DisableWithState, props);
}

interface EnableWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: BuilderLoginScreenRefs;
  _authenticateUser?: _EnableOnPressed;
}

class EnableState extends ObjectObservable {
  private _disable: boolean = false;
  public _hover: boolean = false;
  public _focus: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
  public get focus(): boolean {
    return this._focus;
  }
  public setFocus(val: boolean) {
    let isValChanged: boolean = this._focus !== val;

    if (!isValChanged) {
      return;
    }

    this._focus = val;

    this.fire("focus", this);
  }
}

class _EnableWithState extends ObservableComponent<EnableWithStateProps> {
  enableFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: EnableWithStateProps) {
    super(props);

    this.initState();
  }
  public get enable(): EnableState {
    return this.props.d3eState.enable;
  }
  public get d3eState(): BuilderLoginScreenRefs {
    return this.props.d3eState;
  }
  public get _authenticateUser(): _EnableOnPressed {
    return this.props._authenticateUser;
  }
  public initState() {
    super.initState();

    this.updateObservable("enable", null, this.enable);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(
      ["enable", "enable.", "enable.focus", "enable.hover"],
      this.rebuild
    );
  }
  public enableOnEnter(event): void {
    return this.enable.setHover(true);
  }
  public enableOnExit(event): void {
    return this.enable.setHover(false);
  }
  public enableOnFocusChange(val): void {
    return this.enable.setFocus(val);
  }
  public dispose(): void {
    this.enable.setHover(false);

    this.enable.setFocus(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      constraints: new ui.BoxConstraints({ minWidth: 388 }),
      child: Button({
        padding: cStyle.tButtonPrimaryButtonPaddingOn,
        decoration: this.enable.hover
          ? cStyle.tButtonPrimaryButtonDecorationOnHover
          : this.enable.focus
          ? cStyle.tButtonPrimaryButtonDecorationOnFocus
          : cStyle.tButtonPrimaryButtonDecorationOn,
        disable: this.enable.disable,
        onPressed: () => {
          this._authenticateUser(this.d3eState);
        },
        onFocusChange: (val) => {
          this.enableOnFocusChange(val);
        },
        child: TextView({ data: "Log In" }),
        onEnter: (event) => {
          this.enableOnEnter(event);
        },
        onExit: (event) => {
          this.enableOnExit(event);
        },
      }),
      className: "xefb",
    });
  }
}
function EnableWithState(props: EnableWithStateProps) {
  return React.createElement(_EnableWithState, props);
}

interface ForgetpasswordWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: BuilderLoginScreenRefs;
  _onForgotPasswordHandler?: _ForgetpasswordOnPressed;
}

class ForgetpasswordState extends ObjectObservable {
  private _disable: boolean = false;
  public _hover: boolean = false;
  public _focus: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
  public get focus(): boolean {
    return this._focus;
  }
  public setFocus(val: boolean) {
    let isValChanged: boolean = this._focus !== val;

    if (!isValChanged) {
      return;
    }

    this._focus = val;

    this.fire("focus", this);
  }
}

class _ForgetpasswordWithState extends ObservableComponent<ForgetpasswordWithStateProps> {
  forgetpasswordFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: ForgetpasswordWithStateProps) {
    super(props);

    this.initState();
  }
  public get forgetpassword(): ForgetpasswordState {
    return this.props.d3eState.forgetpassword;
  }
  public get d3eState(): BuilderLoginScreenRefs {
    return this.props.d3eState;
  }
  public get _onForgotPasswordHandler(): _ForgetpasswordOnPressed {
    return this.props._onForgotPasswordHandler;
  }
  public initState() {
    super.initState();

    this.updateObservable("forgetpassword", null, this.forgetpassword);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(
      [
        "forgetpassword",
        "forgetpassword.",
        "forgetpassword.focus",
        "forgetpassword.hover",
      ],
      this.rebuild
    );
  }
  public forgetpasswordOnEnter(event): void {
    return this.forgetpassword.setHover(true);
  }
  public forgetpasswordOnExit(event): void {
    return this.forgetpassword.setHover(false);
  }
  public forgetpasswordOnFocusChange(val): void {
    return this.forgetpassword.setFocus(val);
  }
  public dispose(): void {
    this.forgetpassword.setHover(false);

    this.forgetpassword.setFocus(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return Button({
      decoration: this.forgetpassword.focus
        ? cStyle.tButtonUnderlineButtonDecorationOnFocus
        : new ui.BoxDecoration({}),
      disable: this.forgetpassword.disable,
      onPressed: () => {
        this._onForgotPasswordHandler(this.d3eState);
      },
      onFocusChange: (val) => {
        this.forgetpasswordOnFocusChange(val);
      },
      child: TextView({ data: "Forgot Password?", className: "x51e" }),
      onEnter: (event) => {
        this.forgetpasswordOnEnter(event);
      },
      onExit: (event) => {
        this.forgetpasswordOnExit(event);
      },
      className: "x56",
    });
  }
}
function ForgetpasswordWithState(props: ForgetpasswordWithStateProps) {
  return React.createElement(_ForgetpasswordWithState, props);
}

class _BuilderLoginScreenState extends ObservableComponent<BuilderLoginScreenProps> {
  d3eState: BuilderLoginScreenRefs = new BuilderLoginScreenRefs();
  email: string = "";
  password: string = "";
  errorMessage: string = "";
  showError: boolean = false;
  hitRequest: boolean = false;
  emialInfo: boolean = false;
  passwordInfo: boolean = false;
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: BuilderLoginScreenProps) {
    super(props);

    this.initState();
  }
  public initState() {
    super.initState();

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(
      [
        "email",
        "emialInfo",
        "hitRequest",
        "password",
        "passwordInfo",
        "showError",
      ],
      this.rebuild
    );
  }
  public setEmail(val: string): void {
    let isValChanged: boolean = this.email !== val;

    if (!isValChanged) {
      return;
    }

    this.email = val;

    this.fire("email", this);
  }
  public setPassword(val: string): void {
    let isValChanged: boolean = this.password !== val;

    if (!isValChanged) {
      return;
    }

    this.password = val;

    this.fire("password", this);
  }
  public setErrorMessage(val: string): void {
    let isValChanged: boolean = this.errorMessage !== val;

    if (!isValChanged) {
      return;
    }

    this.errorMessage = val;

    this.fire("errorMessage", this);
  }
  public setShowError(val: boolean): void {
    let isValChanged: boolean = this.showError !== val;

    if (!isValChanged) {
      return;
    }

    this.showError = val;

    this.fire("showError", this);
  }
  public setHitRequest(val: boolean): void {
    let isValChanged: boolean = this.hitRequest !== val;

    if (!isValChanged) {
      return;
    }

    this.hitRequest = val;

    this.fire("hitRequest", this);
  }
  public setEmialInfo(val: boolean): void {
    let isValChanged: boolean = this.emialInfo !== val;

    if (!isValChanged) {
      return;
    }

    this.emialInfo = val;

    this.fire("emialInfo", this);
  }
  public setPasswordInfo(val: boolean): void {
    let isValChanged: boolean = this.passwordInfo !== val;

    if (!isValChanged) {
      return;
    }

    this.passwordInfo = val;

    this.fire("passwordInfo", this);
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ScrollView2({
      child: ui.Column({
        children: [
          TextView({
            data: "Log in",
            style: new ui.TextStyle({
              color: cStyle.tTextViewBoldHeadingColorOn,
              fontSize: cStyle.tTextViewBoldHeadingFontSizeOn,
              fontWeight: cStyle.tTextViewBoldHeadingFontWeightOn,
            }),
            className: "x4e hc",
            key: "0",
          }),
          ui.Container({
            width: 390,
            child: LabelWithStringField({
              name: "Email Address",
              value: this.email,
              autoFocus: true,
              placeHolder: "YourName@Company.com",
              errors: this.emialInfo ? ["Please enter an email address"] : [],
              onTap: () => {
                this.setPasswordError(this.d3eState);
              },
              onChanged: (text) => {
                this.emailFieldonChanged(text, this.d3eState);

                this.setSateEmail(text, this.d3eState);
              },
            }),
            key: "1",
            className: "x55 hc",
          }),
          ui.Container({ className: "xc5a hc vc", key: "2" }),
          ui.Container({
            width: 390,
            child: LabelWithStringField({
              name: "Password",
              value: this.password,
              placeHolder: "Password",
              obscureText: true,
              errors: this.passwordInfo ? ["Please enter Password"] : [],
              onTap: () => {
                this.setEmailError(this.d3eState);
              },
              onSubmitted: (value) => {
                this.onLoginWithEnterKey(value, this.d3eState);
              },
              onChanged: (text) => {
                this.passwordFieldonChanged(text, this.d3eState);

                this.setSatePassword(text, this.d3eState);
              },
            }),
            key: "3",
            className: "xa9 hc",
          }),
          ui.Container({ className: "x24a hc vc", key: "4" }),
          ui.Row({
            mainAxisAlignment: ui.MainAxisAlignment.end,
            children: [
              ForgetpasswordWithState({
                d3eState: this.d3eState,
                _onForgotPasswordHandler: this.onForgotPasswordHandler,
                key: "0",
              }),
            ],
            className: "xc7 hc",
            key: "5",
          }),
          ui.Container({ className: "x9ef hc vc", key: "6" }),
          this.showError
            ? TextView({
                data: "Invalid authentication details. Please Enter valid details.",
                textAlign: cStyle.tTextViewErrorMessageTextAlignOn,
                style: new ui.TextStyle({
                  color: cStyle.tTextViewErrorMessageColorOn,
                  fontSize: cStyle.tTextViewErrorMessageFontSizeOn,
                }),
                className: "xc4 hc",
              })
            : [],
          ui.Row({
            mainAxisAlignment: ui.MainAxisAlignment.center,
            children: [
              !this.hitRequest
                ? ui.Container({
                    child: EnableWithState({
                      d3eState: this.d3eState,
                      _authenticateUser: this.authenticateUser,
                    }),
                  })
                : ui.Container({
                    child: DisableWithState({ d3eState: this.d3eState }),
                  }),
            ],
            className: "x6ae hc h",
            key: "8",
          }),
          ui.Container({ className: "xf0 hc vc", key: "9" }),
          ui.Row({
            mainAxisAlignment: ui.MainAxisAlignment.center,
            children: [
              TextView({ data: "Not a User?  ", className: "xdc2", key: "0" }),
              Signup1WithState({
                d3eState: this.d3eState,
                _onSignupHandler: this.onSignupHandler,
                key: "1",
              }),
            ],
            className: "xac hc h",
            key: "10",
          }),
        ],
        className: ui.join(this.props.className, "BuilderLoginScreen x72 hc h"),
        ...copyBaseUIProps(this.props),
      }),
      scrollDirection: ui.Axis.vertical,
      className: "BuilderLoginScreen x72 hc h",
      controller: this.d3eState.mainScrollController,
    });
  }
  public onForgotPasswordHandler = (d3eState: BuilderLoginScreenRefs): void => {
    this.onForgot();
  };
  public onSignupHandler = (d3eState: BuilderLoginScreenRefs): void => {
    this.onSignup();
  };
  public setSateEmail = (
    value: string,
    d3eState: BuilderLoginScreenRefs
  ): void => {
    this.setEmail(value);
  };
  public setSatePassword = (
    value: string,
    d3eState: BuilderLoginScreenRefs
  ): void => {
    this.setPassword(value);
  };
  public setEmailError = (d3eState: BuilderLoginScreenRefs): void => {
    this.setPasswordInfo(false);
  };
  public onLoginWithEnterKey = (
    value: string,
    d3eState: BuilderLoginScreenRefs
  ): void => {
    this.doAuthenticateUser();
  };
  public setPasswordError = (d3eState: BuilderLoginScreenRefs): void => {
    this.setEmialInfo(false);

    this.setShowError(false);
  };
  public authenticateUser = (d3eState: BuilderLoginScreenRefs): void => {
    this.doAuthenticateUser();
  };
  public doAuthenticateUser = async (): Promise<void> => {
    this.setHitRequest(true);

    this.setShowError(false);

    if (this.email.isEmpty) {
      this.setEmialInfo(true);

      this.setHitRequest(false);
    }

    if (this.password.isEmpty) {
      this.setPasswordInfo(true);

      this.setHitRequest(false);
    }

    let loginResult: LoginResult =
      await Query.get().loginDeveloperUserTypeWithEmailAndPassword(
        UsageConstants.QUERY_LOGINDEVELOPERUSERTYPEWITHEMAILANDPASSWORD_BUILDERLOGINSCREEN_EVENTHANDLERS_DOAUTHENTICATEUSER_BLOCK,
        { email: this.email.toLowerCase(), password: this.password }
      );

    if (loginResult.success) {
      //  implement remeber functionality

      //  if (this.rememberMe) {

      //      Device.put('email', this.email);

      //      Device.put('password', this.password);

      //  }

      this.onLoginUser(loginResult.userObject as Developer);
    } else {
      this.setErrorMessage(loginResult.failureMessage);

      if (this.errorMessage === loginResult.failureMessage) {
        this.setShowError(true);
      }

      this.setHitRequest(false);
    }
  };
  public get onSignup(): _BuilderLoginScreenOnSignup {
    return this.props.onSignup;
  }
  public get onForgot(): _BuilderLoginScreenOnForgot {
    return this.props.onForgot;
  }
  public get onLoginUser(): _BuilderLoginScreenOnLoginUser {
    return this.props.onLoginUser;
  }
  public emailFieldonChanged = (
    val: string,
    d3eState: BuilderLoginScreenRefs
  ): void => {
    this.setEmail(val);
  };
  public passwordFieldonChanged = (
    val: string,
    d3eState: BuilderLoginScreenRefs
  ): void => {
    this.setPassword(val);
  };
  public get disable() {
    return this.d3eState.disable;
  }
  public get enable() {
    return this.d3eState.enable;
  }
  public get forgetpassword() {
    return this.d3eState.forgetpassword;
  }
  public get signup1() {
    return this.d3eState.signup1;
  }
}
export default function BuilderLoginScreen(props: BuilderLoginScreenProps) {
  return React.createElement(_BuilderLoginScreenState, props);
}
